import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import { CART_URL } from "@/store/constants";

type ToastType = "success" | "error";

interface ToastItem {
  id: number;
  type: ToastType;
  message: ReactNode;
  showCartLink: boolean;
}

interface ToastOptions {
  showCartLink?: boolean;
}

type ShowToast = (type: ToastType, message: ReactNode, options?: ToastOptions) => void;

const ToastContext = createContext<ShowToast | null>(null);

/** Access the imperative `showToast(type, message, opts?)` function. */
export function useToast(): ShowToast {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback<ShowToast>(
    (type, message, options) => {
      const id = nextId.current++;
      const showCartLink = !!options?.showCartLink;
      setToasts((prev) => [...prev, { id, type, message, showCartLink }]);
      window.setTimeout(() => remove(id), showCartLink ? 8000 : 4000);
    },
    [remove]
  );

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <div className="agd:fixed agd:bottom-6 agd:right-6 agd:z-[9999] agd:flex agd:flex-col agd:gap-2.5 max-agd:sm:agd:left-3 max-agd:sm:agd:right-3">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={
              t.type === "success"
                ? "agd:flex agd:min-w-[280px] agd:max-w-[360px] agd:items-start agd:gap-2.5 agd:rounded-lg agd:border-l-4 agd:border-accent agd:bg-card agd:px-4 agd:py-3.5 agd:text-sm agd:font-medium agd:text-foreground agd:shadow-lg max-agd:sm:agd:max-w-none"
                : "agd:flex agd:min-w-[280px] agd:max-w-[360px] agd:items-start agd:gap-2.5 agd:rounded-lg agd:bg-destructive agd:px-4 agd:py-3.5 agd:text-sm agd:font-medium agd:text-white agd:shadow-lg max-agd:sm:agd:max-w-none"
            }
          >
            <span className={t.type === "success" ? "agd:text-accent" : "agd:text-white"}>
              {t.type === "success" ? <Check size={18} /> : <X size={18} />}
            </span>
            <span className="agd:flex agd:flex-col agd:gap-1.5">
              <span>{t.message}</span>
              {t.showCartLink ? (
                <a href={CART_URL} className="agd:font-semibold agd:text-primary agd:no-underline hover:agd:underline">
                  View cart &rarr;
                </a>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
